// src/ui/Auth.tsx
import { h } from "preact";
import { useState } from "preact/hooks";
import { Form, FormField } from "./components/Form";
import { useForm } from "./hooks/useForm";
import { useAuth } from "./context/AuthContext";

interface AuthFormValues {
    email: string;
    password: string;
}

export default function Auth() {
    const [mode, setMode] = useState<"login" | "signup">("login");
    const [message, setMessage] = useState<string>("");
    const { login, signup } = useAuth();

    const { values, errors, isSubmitting, handleChange, handleSubmit } =
        useForm<AuthFormValues>({
            initialValues: {
                email: "",
                password: "",
            },
            validate: (values: AuthFormValues) => {
                const errors: Partial<Record<keyof AuthFormValues, string>> =
                    {};
                if (!values.email) {
                    errors.email = "Email is required";
                } else if (
                    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
                        values.email,
                    )
                ) {
                    errors.email = "Invalid email address";
                }
                if (!values.password) {
                    errors.password = "Password is required";
                } else if (values.password.length < 8) {
                    errors.password = "Password must be at least 8 characters";
                }
                return errors;
            },
            onSubmit: async (values: AuthFormValues) => {
                try {
                    setMessage("");
                    if (mode === "login") {
                        await login(values.email, values.password);
                    } else {
                        await signup(values.email, values.password);
                    }
                    // Auth context will handle the state update
                } catch (err) {
                    setMessage(
                        err instanceof Error
                            ? err.message
                            : "Authentication failed",
                    );
                }
            },
        });

    return (
        <div className="auth-container">
            <h2>{mode === "login" ? "Login" : "Sign Up"}</h2>

            {message && (
                <div
                    className={`message ${message.includes("successful") ? "success" : "error"}`}
                >
                    {message}
                </div>
            )}

            <Form handleSubmit={handleSubmit} className="auth-form">
                <FormField
                    label="Email"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange("email")}
                    error={errors.email}
                    required
                />
                <FormField
                    label="Password"
                    name="password"
                    type="password"
                    value={values.password}
                    onChange={handleChange("password")}
                    error={errors.password}
                    required
                />
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="submit-button"
                >
                    {isSubmitting
                        ? `${mode === "login" ? "Logging in" : "Signing up"}...`
                        : mode === "login"
                          ? "Login"
                          : "Sign Up"}
                </button>
            </Form>

            <div className="auth-toggle">
                {mode === "login" ? (
                    <p>
                        Don't have an account?{" "}
                        <button
                            onClick={() => setMode("signup")}
                            className="link-button"
                        >
                            Sign Up
                        </button>
                    </p>
                ) : (
                    <p>
                        Already have an account?{" "}
                        <button
                            onClick={() => setMode("login")}
                            className="link-button"
                        >
                            Login
                        </button>
                    </p>
                )}
            </div>
        </div>
    );
}
